<script lang="ts">
  import CategoryCard from '$components/category-card.svelte';
  import { cn } from '$lib/utils';
  import type {
    AssetStoryblok,
    ConfigurationStoryblok,
    NavigationGroupStoryblok,
    RegionalFeaturedPageStoryblok
  } from '$types/bloks';
  import { onMount } from 'svelte';
  import { getAnchorFromCmsLink, getImageAttributes } from '$lib/utils/cms';
  import { region } from '$lib/region';
  import { user } from '$lib/stores/user';
  import LinkCard from '$components/link-card.svelte';
  import Icon from '$components/ui/icon/icon.svelte';
  import { t } from '$lib/i18n';
  import { buildPath } from '$lib/utils/paths';
  import Button from '$components/ui/button.svelte';
  import StoreSwitcher from '$components/store-switcher.svelte';
  import LangSwitcher from '$components/lang-switcher.svelte';
  import { AUTH_DRAWER_IDS } from '$lib/constants';
  import { drawer } from '$lib/stores/drawer';
  import Accordion from '$components/accordion.svelte';
  import Link from '$components/ui/link.svelte';

  export let configuration: ConfigurationStoryblok;
  let headerContent: ConfigurationStoryblok['header_content'];
  let mainCategories: NavigationGroupStoryblok[] = [];
  let otherCategories: RegionalFeaturedPageStoryblok[] = [];
  let highlightPosition: ConfigurationStoryblok['header_mobile_highlight_position'] =
    configuration.header_mobile_highlight_position || 'up';

  $: isLoggedIn = !!$user;

  onMount(() => {
    headerContent = configuration?.header_content || [];

    if (headerContent) {
      const tempMainCategories: NavigationGroupStoryblok[] = [];
      const tempOtherCategories: RegionalFeaturedPageStoryblok[] = [];

      headerContent.forEach((item) => {
        if (
          item.component === ('navigation-group' as NavigationGroupStoryblok['component']) &&
          item.regions?.includes($region) &&
          item.show_on_mobile
        ) {
          tempMainCategories.push(item as NavigationGroupStoryblok);
        }
        if (
          item.component ===
            ('regional-featured-page' as RegionalFeaturedPageStoryblok['component']) &&
          item.regions?.includes($region) &&
          item.show_on_mobile
        ) {
          tempOtherCategories.push(item as RegionalFeaturedPageStoryblok);
        }
      });

      mainCategories = tempMainCategories;
      otherCategories = tempOtherCategories;
    }
  });

  const getImage = (
    desktopImage: AssetStoryblok | undefined,
    mobileImage: AssetStoryblok | undefined
  ) => {
    const featuredImage = mobileImage || desktopImage;

    if (!featuredImage || !featuredImage?.filename) {
      return { src: '', alt: '' };
    }

    const { src, alt } = getImageAttributes(featuredImage, {
      size: [300, 0]
    });

    return { src, alt };
  };
</script>

<div class="relative h-full overflow-y-auto px-4 pb-4 pt-7">
  <div class="flex flex-col w-full h-full justify-between">
    <div class="flex flex-col gap-2">
      <div
        class="grid items-start gap-4"
        class:grid-cols-1={otherCategories.length === 1}
        class:grid-cols-2={otherCategories.length > 1}
        class:order-first={highlightPosition === 'up'}
        class:order-last={highlightPosition === 'down'}
      >
        {#each otherCategories as story}
          {@const featurePage = (story.feature_page || [])[0]}
          {@const { href } = $getAnchorFromCmsLink(featurePage?.link)}

          {#if featurePage?.mobile_image?.filename || featurePage?.image?.filename}
            {@const { src, alt } = getImage(featurePage?.image, featurePage?.mobile_image)}

            {#if href}
              <CategoryCard
                size="sm"
                {href}
                open_new_tab={featurePage.open_new_tab}
                label={featurePage.name || ''}
                image={{ src, alt }}
                class="w-full h-full aspect-[171/112]"
              />
            {/if}
          {:else if href}
            <CategoryCard
              size="sm"
              {href}
              open_new_tab={featurePage.open_new_tab}
              label={story.content.page_title || story.name}
            />
          {/if}
        {/each}
      </div>

      <div>
        {#each mainCategories as story}
          <Accordion
            title={story.title || ''}
            titleColor={story.title_color}
            variant="header"
            group="mobile-navigation"
          >
            <div class="my-0.5 flex flex-col gap-5">
              {#if story.content && story.content.length > 0}
                {#each story.content as link}
                  {#if link.url}
                    {@const { href } = $getAnchorFromCmsLink(link.url)}
                    <Link
                      {href}
                      target={link.open_new_tab ? '_blank' : undefined}
                      class="font-medium text-md pl-2">{link.label}</Link
                    >
                  {/if}
                {/each}
              {/if}
            </div>
          </Accordion>
        {/each}
      </div>
    </div>

    <div class="mt-7">
      {#if isLoggedIn || configuration.navigation_support_link}
        <div class="grid grid-cols-2 gap-2">
          {#if isLoggedIn}
            <LinkCard href={$buildPath({ path: '/account/profile' })}>
              <Icon icon="user" />
              <span>{$t('topnav.profile')}</span>
            </LinkCard>
          {/if}

          {#if configuration.navigation_support_link}
            {@const { href } = $getAnchorFromCmsLink(configuration.navigation_support_link)}
            <LinkCard {href} class={cn(!isLoggedIn && 'col-span-2')}>
              <Icon icon="support" />
              <span>{$t('topnav.support')}</span>
            </LinkCard>
          {/if}
        </div>
      {/if}

      <div class={cn(isLoggedIn ? 'h-[48px]' : 'h-[108px]')} />
    </div>
  </div>
</div>

<div class="absolute bottom-0 left-0 z-10 w-full border-y border-primary/4 bg-background">
  <div class="flex justify-between gap-2 pt-4 px-2 pb-4">
    <div class="flex items-center">
      <StoreSwitcher />
    </div>
    <div class="flex gap-2 flex-col items-end">
      <LangSwitcher />
    </div>
  </div>
  {#if !isLoggedIn}
    <div class="w-full gap-2 pb-4 px-2">
      <Button
        name={AUTH_DRAWER_IDS.LOGIN}
        on:click={() => {
          drawer.open(AUTH_DRAWER_IDS.LOGIN);
        }}
        class="w-full"
      >
        {$t('topnav.signin')}
      </Button>
    </div>
  {/if}
</div>
